import React, {useEffect} from 'react'
import {useSessionCookie} from "../hooks/useSessionCookie";
import {createContext, ReactNode, useState} from "react"
import {AuthUser, User, UserFormData} from "../models/user"
import {useApiService} from "../hooks/useApiService"
import {
    signInWithEmailAndPassword,
    UserCredential,
    signInWithCustomToken,
    signOut,
    signInWithPopup
} from 'firebase/auth'
import {auth as firebaseAuth, auth} from "../firebase"
import {useFeedback} from "../hooks/useFeedback"
import {
    register as _register,
    logout as _logout,
    currentUser,
    handoffToken,
    authenticateWithProvider as _authenticateWithProvider
} from "../services/api/auth"
import {deleteAccount as _deleteAccount} from "../services/api/user"
import {AuthError} from "firebase/auth"
import {FacebookAuthProvider, GoogleAuthProvider} from "@firebase/auth";
import i18n from "../localization";
import {APP_URL, HOSTNAME} from "../constants";
import {appHost, appRoute, productionMode} from "../utils/routes";

/**
 * After logging out on app.linguascores.com, also sign out of the guest site: checkout signs people
 * in there, and its session isn't shared with the app's, so its pages kept showing the old account.
 */
const signOutOfGuestSite = () => {
    if (productionMode && appHost)
        window.location.href = `https://${HOSTNAME}/logout`
}

export const IAuthContext = createContext({})

const authErrorCodeMessages = [{
    field: 'auth',
    code: 'auth/invalid-credential',
    message: 'Invalid email or password'
}, {
    field: 'auth',
    code: "auth/popup-closed-by-user",
    message: "Sign-in cancelled."
}, {
    field: 'auth',
    code: "auth/account-exists-with-different-credential",
    message: "An account already exists with this email using a different sign-in method."
}, {
    field: 'auth',
    code: "auth/popup-blocked",
    message: "Pop-up was blocked by your browser."
}, {
    field: 'auth',
    code: "auth/network-request-failed",
    message: "Network error. Check your connection."
}]

const googleProvider = new GoogleAuthProvider()
const facebookProvider = new FacebookAuthProvider()

// optional: request extra scopes
googleProvider.addScope("email")
googleProvider.addScope("profile")
facebookProvider.addScope("email")
facebookProvider.addScope("public_profile")

export type AuthContextState = {
    login?: (credentials: {
        email?: string,
        password?: string
    }, userProviderAuthId?: string) => Promise<boolean>,
    loginWith2Fa?: (sessionId: string, code: string) => Promise<void>,
    register?: (userFormData: UserFormData) => Promise<void | AuthUser['paymentRequestResponse']>,
    authenticateWithProvider?: (provider: "google" | "facebook") => Promise<void>,
    logout?: () => Promise<void>,
    deleteAccount?: (confirmation: { password?: string, confirmation?: string }) => Promise<boolean>,
    appEntryUrl?: (path?: string) => Promise<string>,
    signInWithHandoffToken?: (token: string) => Promise<boolean>,
    loading?: boolean
}


const AuthContext = (props: { children: ReactNode }) => {
    const [loading, setLoading] = useState(false)
    const {userSession, setUserSession, clearUserSession} = useSessionCookie()
    const apiService = useApiService()
    const feedback = useFeedback()

    const logout = async () => {
        try {
            let apiClient = await apiService.client(true, true)
            setLoading(true)
            await _logout(apiClient)
            await signOut(auth)
            clearUserSession()
            setLoading(false)
            signOutOfGuestSite()
        } catch (error) {
            setLoading(false)
        }
    }

    /**
     * Permanently deletes the signed-in account. The backend removes the Firebase user too, so
     * the local session is simply torn down afterwards — there is nothing left to log out of.
     */
    const deleteAccount = async (confirmation: { password?: string, confirmation?: string }) => {
        try {
            setLoading(true)
            const apiClient = await apiService.client(true, true)
            await _deleteAccount(apiClient, confirmation)
            await signOut(auth).catch(() => {})
            clearUserSession()
            feedback.showFeedback("ACCOUNT_DELETED", "success")
            setLoading(false)
            signOutOfGuestSite()
            return true
        } catch (error) {
            setLoading(false)
            return false
        }
    }

    /** The session cookie for whoever Firebase has signed in, built from their profile as `login` does. */
    const startSession = async (idToken: string) => {
        const client = await apiService.client(true)
        const userInfoResult = await currentUser(client)
        setUserSession({
            currentUser: userInfoResult,
            token: idToken,
            expiresAt: Date.now() + 36 * 3600 * 1000,
        }, new Date(new Date().getTime() + 36 * 3600 * 1000))
    }

    /**
     * Where to send a signed-in user for the dashboard (or `path`). Checkout runs on the guest site
     * in production, and sign-ins aren't shared between linguascores.com and app.linguascores.com,
     * so from there this hands the session over through /continue. The token rides in the URL
     * fragment, which browsers never send to a server, and /continue strips it straight away.
     */
    const appEntryUrl = async (path = "") => {
        if (!productionMode || appHost)
            return appRoute(path)
        try {
            const client = await apiService.client(true)
            const {firebaseToken} = await handoffToken(client)
            const fragment = new URLSearchParams({token: firebaseToken, next: path})
            return `${APP_URL}/continue#${fragment.toString()}`
        } catch (e) {
            console.log(e)
            return `${APP_URL}/login`
        }
    }

    const signInWithHandoffToken = async (token: string) => {
        try {
            setLoading(true)
            const result = await signInWithCustomToken(auth, token)
            await startSession(await result.user.getIdToken())
            return true
        } catch (e) {
            console.log(e)
            return false
        } finally {
            setLoading(false)
        }
    }

    const login = async (credentials: { email: string, password: string }) => {
        try {
            setLoading(true)
            const loginResult = await signInWithEmailAndPassword(auth, credentials.email, credentials.password)
            const client = await apiService.client(true)
            const userInfoResult = await currentUser(client)
            setUserSession({
                currentUser: userInfoResult,
                token: await loginResult.user.getIdToken(),
                expiresAt: Date.now() + 36 * 3600 * 1000,
            }, new Date(new Date().getTime() + 36 * 3600 * 1000))
            setLoading(false)

        } catch (e) {
            setLoading(false)
            feedback.showFeedback(authErrorCodeMessages.find(value => value.code === (e as AuthError).code)?.message || e['message'], 'danger')
            console.log(e)
        }
    }

    const register = async (userFormData: UserFormData) => {
        try {
            let apiClient = await apiService.client(false, true)
            setLoading(true)
            // Carry the language picked while browsing as a guest into the new account.
            userFormData.language = userFormData.language ?? i18n.language
            const {paymentRequestResponse, ...result} = await _register(apiClient, userFormData)
            const credential = await signInWithCustomToken(auth, result.firebaseToken)

            delete userFormData.confirmPassword
            delete userFormData.password
            // Start the session from the saved account, as login does. The sign-up form alone lacks
            // what the server adds (id, createdAt, …), which crashed Settings ("Invalid time value").
            try {
                await startSession(await credential.user.getIdToken())
            } catch (sessionError) {
                console.log(sessionError)
                setUserSession({
                    currentUser: userFormData,
                    token: result.firebaseToken,
                    expiresAt: Date.now() + 36 * 3600 * 1000,
                }, new Date(new Date().getTime() + 36 * 3600 * 1000))
            }
            setLoading(false)

            if (paymentRequestResponse)
                return paymentRequestResponse

            return true
        } catch (e) {
            setLoading(false)
            if (e.code)
                feedback.showFeedback(authErrorCodeMessages.find(value => value.code === (e as AuthError).code)?.message, "danger")
            else
                feedback.showFeedback(e['message'], "danger")
            console.log(e)
            return false
        }
    }

    const authenticateWithProvider = async (provider: "google" | "facebook") => {
        try {
            let apiClient = await apiService.client(false, true)
            const authResult = await signInWithPopup(firebaseAuth, provider === "facebook" ? facebookProvider : googleProvider)
            const result = await _authenticateWithProvider(apiClient, await authResult.user.getIdToken())

            setUserSession({
                currentUser: result,
                token: result.firebaseToken,
                expiresAt: Date.now() + 36 * 3600 * 1000,
            }, new Date(new Date().getTime() + 36 * 3600 * 1000))
        } catch (e) {
            if (e.code)
                feedback.showFeedback(authErrorCodeMessages.find(value => value.code === (e as AuthError).code)?.message, "danger")
            else
                feedback.showFeedback(e['message'], "danger")
            console.log(e)
        }
    }

    return <IAuthContext.Provider value={{
        login,
        logout,
        deleteAccount,
        appEntryUrl,
        signInWithHandoffToken,
        register,
        authenticateWithProvider,
        loading
    }}>

        {props.children}

    </IAuthContext.Provider>
}

export default AuthContext